import * as React from 'react';
import { computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import * as classNames from 'classnames';
import { toFixed } from 'accounting';
import AnimateHeight from 'react-animate-height';
import Address from '../../../../../common/method/Address';
import { Cart, User } from '../../../stores';
import { PortalSearchBarField } from '../../../components/layout';
import { ShippingMethod } from '../../../../../common/interfaces/search';
import { Picker } from '../../../components/forms';
import { IAppOnlyStore } from '../../../stores/AllStores';

// Styles
const s = require('./PortalCart.css');
const tickMarkIcon = require('./../../../assets/tick-blue.svg');
const expandIcon = require('./../../../assets/expand-icon.svg');
const shoppingBagIcon = require('./../../../assets/shopping-bag-icon.svg');

// Component
@inject('store')
@observer
export class PortalCartOptions extends React.Component<IAppOnlyStore, {}> {
    private shippingMethodLabel = (shippingMethod: ShippingMethod) => {
        return shippingMethod === 'delivery'
            ? 'Delivery'
            : shippingMethod === 'pickup'
            ? 'Will-call pickup'
            : shippingMethod === 'ltl'
            ? 'LTL common carrier'
            : shippingMethod === 'ltlwithliftgate'
            ? 'LTL common carrier - liftgate required'
            : shippingMethod === 'other'
            ? 'Other'
            : 'None selected';
    };

    @computed
    private get customerLTLNumber(): number | null {
        const { user } = this.props.store;

        return user.isCustomer &&
            user.customer &&
            user.customer.listnum !== null &&
            user.customerIsPBI &&
            user.customer.listnum >= 4 &&
            user.customer.listnum <= 7
            ? this.props.store.user.customer.listnum
            : null;
    }

    @computed
    private get minimumLTLTotal(): number {
        return this.customerLTLNumber
            ? this.customerLTLNumber <= 4
                ? 1000
                : this.customerLTLNumber === 5
                ? 1500
                : this.customerLTLNumber === 6
                ? 2000
                : 3000
            : 3000;
    }

    @computed
    private get minimumLTLWithLiftgateTotal(): number {
        return this.customerLTLNumber
            ? this.customerLTLNumber <= 4
                ? 1500
                : this.customerLTLNumber === 5
                ? 2000
                : this.customerLTLNumber === 6
                ? 2500
                : 3000
            : 3000;
    }

    @computed
    private get minimumLTLTotalMet(): boolean {
        const { cart } = this.props.store;

        return cart.selectedShippingMethod === 'ltl'
            ? cart.currentCartTotalCost >= this.minimumLTLTotal
                ? true
                : false
            : cart.selectedShippingMethod === 'ltlwithliftgate'
            ? cart.currentCartTotalCost >= this.minimumLTLWithLiftgateTotal
                ? true
                : false
            : true;
    }

    @computed
    private get orderNotReadyDueToCurrentCartItemsAreNoLongerInStock(): boolean {
        return this.props.store.cart.currentCartItemsAreNoLongerInStock;
    }

    @computed
    private get orderNotReadyDueToNoShippingMethodSelected(): boolean {
        const { cart } = this.props.store;
        return !cart.selectedShipmentMethodIncludesShipping && !cart.selectedShipmentMethodIsPickup;
    }

    @computed
    private get orderNotReadyDueToLTLMinimumTotalNotMet(): boolean {
        const { cart } = this.props.store;
        return cart.selectedShipmentMethodIncludesShipping;
    }

    @computed
    private get isOrderReady(): { ready: boolean; notReadyReason: string } {
        const { cart } = this.props.store;

        if (this.orderNotReadyDueToCurrentCartItemsAreNoLongerInStock) {
            return {
                ready: false,
                notReadyReason: 'Cart items are no longer in stock'
            };
        }

        if (cart.selectedShipmentMethodIncludesShipping) {
            if (cart.selectedShippingAddressId) {
                switch (cart.selectedShippingMethod) {
                    case 'ltl':
                    case 'ltlwithliftgate':
                        return (cart.selectedShippingMethod === 'ltl' && cart.currentCartTotalCost >= this.minimumLTLTotal) ||
                            (cart.selectedShippingMethod === 'ltlwithliftgate' &&
                                cart.currentCartTotalCost >= this.minimumLTLWithLiftgateTotal)
                            ? { ready: true, notReadyReason: null }
                            : { ready: false, notReadyReason: 'LTL minimum order total is not met' };

                    case 'other':
                        return cart.selectedOtherShippingTypeId !== null &&
                            cart.selectedOtherShippingAccountNumber.trim() !== ''
                            ? { ready: true, notReadyReason: null }
                            : {
                                  ready: false,
                                  notReadyReason:
                                      cart.selectedOtherShippingTypeId === null
                                          ? 'Shipping carrier is required'
                                          : 'Shipping account number is required'
                              };

                    default:
                        return { ready: true, notReadyReason: null };
                }
            } else {
                return {
                    ready: false,
                    notReadyReason: 'Shipping address is required'
                };
            }
        } else if (cart.selectedShipmentMethodIsPickup) {
            return cart.selectedPickupWarehouse !== null
                ? { ready: true, notReadyReason: null }
                : { ready: false, notReadyReason: 'No pickup warehouse selected' };
        } else {
            return {
                ready: false,
                notReadyReason: 'No shipping method selected'
            };
        }
    }

    render() {
        const { cart, user, warehouse } = this.props.store;

        return (
            <div className={s.cartOptions}>
                <div className={s.shippingAddress} onClick={this.handleShippingChoicesContainerClick}>
                    <PortalSearchBarField
                        className={s.poNumberFieldGroup}
                        field="poNumber"
                        label="PO Number"
                        value={cart.selectedPoNumber}
                        onChange={this.handlePoNumberChange}
                        lightness={0}
                    />

                    {user.customerIsPAC && (
                        <Picker
                            show={cart.showInsuranceJob}
                            onValueSelectedChange={this.handleInsuranceJobSelect}
                            valueSelected={cart.isInsuranceJob}
                            options={[
                                {
                                    name: 'Insurance job',
                                    options: [
                                        {
                                            key: 'none',
                                            value: false,
                                            title: 'No',
                                            subline: 'Parts not for insurance job'
                                        },
                                        {
                                            key: 'select',
                                            value: true,
                                            title:
                                                cart.isInsuranceJob && cart.selectedInsuranceJob !== null
                                                    ? 'Yes, change insurance company'
                                                    : 'Yes, select insurance company',
                                            subline:
                                                cart.isInsuranceJob && cart.selectedInsuranceJob !== null
                                                    ? 'Selected "' + cart.selectedInsuranceJob.insuranceCode + '"'
                                                    : 'Parts for insurance job'
                                        }
                                    ]
                                }
                            ]}
                            field={{
                                above: false,
                                label: 'Insurance job',
                                value:
                                    cart.isInsuranceJob && cart.selectedInsuranceJob !== null
                                        ? `Insurance: "${cart.selectedInsuranceJob.insuranceCode}"`
                                        : cart.isInsuranceJob
                                        ? 'Parts for insurance job'
                                        : 'Not an insurance job',
                                onFocus: this.handleInsuranceJobFieldFocus
                            }}
                        />
                    )}

                    <Picker
                        show={cart.showShippingMethods}
                        onValueSelectedChange={this.handleShippingMethodSelect}
                        valueSelected={cart.selectedShippingMethod}
                        options={[
                            {
                                name: 'Shipping methods',
                                options: [
                                    ...(cart.selectedShippingMethod === null
                                        ? [
                                              {
                                                  key: 'none',
                                                  value: null,
                                                  title: 'None',
                                                  subline: 'No shipping method selected'
                                              }
                                          ]
                                        : []),
                                    {
                                        key: 'pickup',
                                        value: 'pickup',
                                        title: this.shippingMethodLabel('pickup'),
                                        subline: 'Same day pickup for orders placed before 12:00pm'
                                    },
                                    {
                                        key: 'delivery',
                                        value: 'delivery',
                                        title: this.shippingMethodLabel('delivery'),
                                        subline: 'Same day delivery for orders placed before 12:00pm'
                                    },
                                    ...(user.customerIsPBI
                                        ? [
                                              {
                                                  key: 'ltl',
                                                  value: 'ltl',
                                                  title: this.shippingMethodLabel('ltl'),
                                                  subline: 'A minimum $' + this.minimumLTLTotal + ' order is required'
                                              },
                                              {
                                                  key: 'ltlwithliftgate',
                                                  value: 'ltlwithliftgate',
                                                  title: this.shippingMethodLabel('ltlwithliftgate'),
                                                  subline:
                                                      'A minimum $' + this.minimumLTLWithLiftgateTotal + ' order is required'
                                              }
                                          ]
                                        : []),
                                    ...(user.customerIsPBI
                                        ? [
                                              {
                                                  key: 'other',
                                                  value: 'other',
                                                  title: this.shippingMethodLabel('other'),
                                                  subline: 'Provide your own shipping instructions'
                                              }
                                          ]
                                        : [])
                                ]
                            }
                        ]}
                        field={{
                            above: false,
                            label: 'Shipping method',
                            value: this.shippingMethodLabel(cart.selectedShippingMethod),
                            onFocus: this.handleShippingMethodFieldFocus
                        }}
                    />

                    {cart.selectedShippingMethod !== null && (
                        <Picker
                            show={cart.showShippingAddressesOrWarehouses}
                            onValueSelectedChange={this.handleShippingAddressOrWarehouseSelect}
                            valueSelected={
                                cart.selectedShipmentMethodIsPickup
                                    ? cart.selectedPickupWarehouse
                                    : cart.selectedShippingAddressId
                            }
                            options={[
                                cart.selectedShipmentMethodIsPickup
                                    ? {
                                          name: 'Warehouses',
                                          options: warehouse.customerPickupWarehouses.map((whs) => ({
                                              key: 'warehouse-' + whs.whsCode,
                                              value: whs,
                                              dim: !whs.whsAllCartItemsAvailable,
                                              title: whs.whsName,
                                              subline: whs.whsAllCartItemsAvailable
                                                  ? 'All cart items are available for pickup'
                                                  : 'Not all cart items are available in this warehouse'
                                          }))
                                      }
                                    : {
                                          name: 'Shipping addresses',
                                          options: user.customerAddresses.shipping.map((addr) => ({
                                              key: 'address-' + addr.addressId,
                                              value: addr.addressId,
                                              title: addr.address,
                                              subline: Address.format(addr)
                                          }))
                                      }
                            ]}
                            field={{
                                above: false,
                                label: cart.selectedShipmentMethodIsPickup ? 'Warehouse to pick up from' : 'Shipping address',
                                value: cart.selectedShipmentMethodIsPickup
                                    ? cart.selectedPickupWarehouse !== null
                                        ? cart.selectedPickupWarehouse.whsName || ''
                                        : ''
                                    : cart.selectedShippingAddressName || '',
                                onFocus: this.handleShippingAddressOrWarehosueFieldFocus
                            }}
                        />
                    )}

                    <AnimateHeight
                        duration={300}
                        height={
                            cart.selectedShippingAddressDetails !== null &&
                            ['delivery', 'ltl', 'ltlwithliftgate', 'other'].indexOf(cart.selectedShippingMethod) !== -1
                                ? 'auto'
                                : 0
                        }
                    >
                        <div className={s.shippingAddressDetails}>
                            <p>{cart.selectedShippingAddressDetails}</p>
                        </div>
                    </AnimateHeight>
                </div>

                {cart.currentCartItemsAreNoLongerInStock && (
                    <div className={s.shippingHours}>
                        <p className={s.important}>
                            Cannot complete order request
                            <br />
                            as some items are out of stock.
                            <br />
                            <button onClick={this.handleOpenWarehousesLocationsModal} className={s.callForAvailability}>
                                Please call for availability →
                            </button>
                        </p>
                    </div>
                )}

                {cart.currentCartItemsAreNoLongerInStock === false && this.minimumLTLTotalMet === false && (
                    <div className={s.shippingHours}>
                        <p className={s.important}>
                            {'A minimum $' +
                                (cart.selectedShippingMethod === 'ltl'
                                    ? this.minimumLTLTotal
                                    : this.minimumLTLWithLiftgateTotal) +
                                ' order is required'}
                        </p>
                    </div>
                )}

                {((cart.selectedShippingMethod === 'ltl' || cart.selectedShippingMethod === 'ltlwithliftgate') &&
                    this.minimumLTLTotalMet === true) ||
                    (cart.selectedShippingMethod === 'other' && (
                        <div className={s.shippingHours}>
                            <p>
                                All orders will be processed and shipped
                                <br />
                                based on order-type cut off times.
                            </p>
                        </div>
                    )) ||
                    (cart.currentCartItemsAreNoLongerInStock === false &&
                        this.minimumLTLTotalMet === true &&
                        cart.selectedShippingMethod !== 'other' &&
                        cart.selectedShippingAddressId !== null && (
                            <div className={s.shippingHours}>
                                {cart.selectedShipmentMethodIsPickup &&
                                cart.selectedPickupWarehouse.whsAllCartItemsAvailable ? (
                                    <p>
                                        If order is placed before 12:00pm, it will be ready for pickup after 2:30pm.
                                        <br />
                                        If order is placed after 12:00pm, it will be for next day pickup after 9:30am.
                                    </p>
                                ) : cart.selectedShipmentMethodIsPickup &&
                                  cart.selectedPickupWarehouse.whsAllCartItemsAvailable === false ? (
                                    <p>
                                        Not all cart items are available in the selected warehouse.
                                        <br />
                                        Order will be ready for pickup next day after 12:00pm.
                                    </p>
                                ) : cart.selectedShipmentMethodIncludesShipping ? (
                                    <p>
                                        After placing your order, a customer service representative will confirm your delivery or pick-up time.
                                        <br />
                                        Orders placed before 11:00 AM typically ship the same day on our second delivery route, if your address receives two deliveries daily.
                                        <br />
                                        Orders requiring internal transfers or stock checks may ship the next business day.
                                        <br />
                                        Locations with one daily delivery will be scheduled for the next available route.
                                        <br />
                                        Questions? Contact us at <a href="mailto:orders@pacauto.com">orders@pacauto.com</a>.
                                    </p>
                                ) : null}
                            </div>
                        ))}

                {cart.selectedShippingMethod === 'other' && (
                    <div className={s.shippingOtherDetails}>
                        <div>
                            <Picker
                                show={cart.showOtherShippingTypes}
                                onContainerClick={this.handleOtherShippingTypePickerClick}
                                onValueSelectedChange={this.handleOtherShippingTypeChange}
                                valueSelected={cart.selectedOtherShippingTypeId}
                                options={[
                                    {
                                        name: 'Shipping carrier',
                                        options: cart.otherShippingTypes.map((type) => ({
                                            key: 'carrier-' + type.id,
                                            value: type.id,
                                            title: type.name
                                        }))
                                    }
                                ]}
                                field={{
                                    above: false,
                                    label: 'Shipping carrier',
                                    onFocus: this.handleOtherShippingTypeFieldFocus,
                                    value: cart.selectedOtherShippingType
                                        ? cart.selectedOtherShippingType.name
                                        : 'None selected'
                                }}
                            />

                            <PortalSearchBarField
                                field="otherShippingAccount"
                                label="Shipping account number"
                                value={cart.selectedOtherShippingAccountNumber}
                                onChange={this.handleOtherShippingAccountChange}
                                lightness={0}
                            />

                            <textarea
                                placeholder="Shipping instructions (optional)"
                                value={cart.selectedOtherShippingInstructions}
                                onChange={this.handleOtherShippingInstructionsChange}
                            />
                        </div>
                    </div>
                )}

                <div className={s.placeOrderContainer}>
                    <button
                        onClick={this.handlePlaceOrder}
                        className={classNames(s.placeOrderButton, {
                            [s.active]: this.isOrderReady.ready
                        })}
                    >
                        <span>Place an order $</span>
                        <strong>{toFixed(cart.currentCartTotalCost, 2)}</strong>
                        <img src={shoppingBagIcon} alt="" />
                    </button>

                    <p>
                        {this.isOrderReady.notReadyReason
                            ? this.isOrderReady.notReadyReason
                            : cart.currentCartHasItemsWithoutStock
                            ? 'Please call to confirm ETA'
                            : ''}
                    </p>
                </div>
            </div>
        );
    }

    @action
    private handleOpenWarehousesLocationsModal = () => {
        this.props.store.warehouse.showWarehousesLocations = true;
    };

    private handleShippingChoicesContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    @action
    private handleInsuranceJobFieldFocus = () => {
        this.props.store.cart.showInsuranceJob = true;
        this.props.store.cart.showShippingMethods = false;
        this.props.store.cart.showShippingAddressesOrWarehouses = false;
        this.props.store.cart.showOtherShippingTypes = false;
    };

    @action
    private handleShippingMethodFieldFocus = () => {
        this.props.store.cart.showShippingMethods = true;
        this.props.store.cart.showInsuranceJob = false;
        this.props.store.cart.showShippingAddressesOrWarehouses = false;
        this.props.store.cart.showOtherShippingTypes = false;
    };

    @action
    private handleInsuranceJobSelect = (isInsuranceJob: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.cart.isInsuranceJob = isInsuranceJob;
        this.props.store.insurance.showInsuranceJobsList = isInsuranceJob;

        if (isInsuranceJob === false) {
            this.props.store.cart.selectedInsuranceJob = null;
        }

        this.props.store.cart.showInsuranceJob = false;
    };

    @action
    private handleShippingMethodSelect = (selectedMethod: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.cart.selectedShippingMethod = selectedMethod;
        this.props.store.cart.showShippingMethods = false;
    };

    @action
    private handleShippingAddressOrWarehosueFieldFocus = () => {
        this.props.store.cart.showInsuranceJob = false;
        this.props.store.cart.showShippingMethods = false;
        this.props.store.cart.showShippingAddressesOrWarehouses = true;
        this.props.store.cart.showOtherShippingTypes = false;
    };

    @action
    private handleShippingAddressOrWarehouseSelect =
        (selectedAddressOrWarehouse: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
            const { cart } = this.props.store;

            if (cart.selectedShipmentMethodIsPickup) {
                cart.selectedPickupWarehouse = selectedAddressOrWarehouse;
            } else {
                cart.selectedShippingAddressId = selectedAddressOrWarehouse;
            }
            cart.showShippingAddressesOrWarehouses = false;
        };

    private handleOtherShippingTypePickerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    @action
    private handleOtherShippingTypeFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.stopPropagation();
        this.props.store.cart.showInsuranceJob = false;
        this.props.store.cart.showShippingMethods = false;
        this.props.store.cart.showShippingAddressesOrWarehouses = false;
        this.props.store.cart.showOtherShippingTypes = true;
    };

    @action
    private handleOtherShippingTypeChange = (otherShippingTypeId: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.cart.selectedOtherShippingTypeId = otherShippingTypeId;
        this.props.store.cart.showOtherShippingTypes = false;
    };

    @action
    private handlePoNumberChange = (field: string, value: string) => {
        this.props.store.cart.selectedPoNumber = value;
    };

    @action
    private handleOtherShippingAccountChange = (field: string, value: string) => {
        this.props.store.cart.selectedOtherShippingAccountNumber = value;
    };

    @action
    private handleOtherShippingInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.props.store.cart.selectedOtherShippingInstructions = e.currentTarget.value;
    };

    @action
    private handlePlaceOrder = () => {
        if (this.isOrderReady.ready) {
            this.props.store.checkout.beginCheckout();
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/portal/PortalCart/PortalCartOptions.tsx